<template>
  <div
    :class="[
      'wizard-nav-container',
      {
        'wizard-nav-overflow-left': overflowLeft,
        'wizard-nav-overflow-right': overflowRight
      }
    ]"
    @scroll="onScroll"
  >
    <div class="wizard-nav">
      <div class="wizard-nav-main">
        <slot name="progress-bar">
          <ProgressBar
            :transitionDuration="transitionDuration"
            :class="progBarClass"
          />
          <!-- Display a full-width track for the progress bar -->
          <ProgressBar v-if="showProgBarTrack"
            :all-steps-complete="true"
            :class="[
              progBarClass,
              'wizard-prog-bar-track',
              progBarTrackClass
            ]"
          />
        </slot>

        <!-- eslint-disable vuejs-accessibility/interactive-supports-focus
          Disabling because child buttons are focusable.
        -->
        <ul
          class="wizard-nav-buttons"
          role="tablist"
          :aria-label="ariaLabel"
          @keydown.left="prevStep"
          @keydown.right="tryNextStep"
        >
          <li v-for="({ name, visited }, i) in steps"
            :key="name"
            :style="{
              width: `${100 / steps.length}%`,
              visibility: hideUnvisited && !visited ? 'hidden' : 'visible'
            }"
            ref="navItem"
          >
            <ReflectAttrs
              v-bind="{ number: i + 1 }"
              v-slot="{ number }"
            >
              <slot name="nav-button"
                v-bind="{ name, number }"
              >
                <NavButton
                  :step-name="name"
                  :class="navButtonClass"
                  :active-class="navButtonActiveClass"
                  :locked-class="navButtonLockedClass"
                  :unlocked-class="navButtonUnlockedClass"
                  :transition-duration="transitionDuration"
                >
                  <slot name="nav-button-content"
                    v-bind="{ name, number }"
                  ></slot>
                </NavButton>
              </slot>
            </ReflectAttrs>
          </li>
        </ul>
        <!-- eslint-enable vuejs-accessibility/interactive-supports-focus -->
      </div>

      <div v-if="showLabels"
        role="presentation"
        class="wizard-nav-labels"
      >
        <div v-for="({ name, visited }) in steps"
          :key="name"
          :class="[
            'wizard-nav-label',
            navLabelClass
          ]"
          :style="{
            width: `${100 / steps.length}%`,
            visibility: hideUnvisited && !visited ? 'hidden' : 'visible'
          }"
        >{{ name }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';
import {
  mapInstanceActions as mapActions, mapInstanceGetters as mapGetters, mapInstanceState as mapState
} from '@urbn/vuex-helpers';
import ReflectAttrs from '../helpers/ReflectAttrs';
import { STORE_NAMESPACE_KEY } from './const';
import NavButton from './NavButton';
import ProgressBar from './ProgressBar';
import { NEXT_STEP, PREV_STEP } from '../../store/modules/formWizard/actions';
import { HIGHEST_UNLOCKED_STEP_INDEX } from '../../store/modules/formWizard/getters';
import { ACTIVE_STEP_INDEX, STEPS } from '../../store/modules/formWizard/state';

const handleOverflowDebounced = debounce((vm) => {
  vm.handleOverflow();
}, 100);

const onResizeDebounced = debounce((vm) => {
  vm.handleOverflow();
  vm.scrollToActive();
}, 250);

export default {
  inject: [STORE_NAMESPACE_KEY],

  props: {
    ariaLabel: {
      type: String,
      default: 'Wizard'
    },

    /**
     * Don't show nav buttons until they are visited using Next.
     */
    hideUnvisited: {
      type: Boolean,
      default: () => false
    },

    navButtonClass: String,
    navButtonActiveClass: String,
    navButtonLockedClass: String,
    navButtonUnlockedClass: String,
    navLabelClass: String,
    progBarClass: String,
    progBarTrackClass: String,

    /**
     * Show labels below the nav buttons.
     */
    showLabels: {
      type: Boolean,
      default: () => true
    },

    /**
     * Show a light-colored track for the progress bar.
     */
    showProgBarTrack: {
      type: Boolean,
      default: () => false
    },

    /**
     * Specify the duration for CSS transitions between nav buttons. This controls
     * how long it takes the progress bar to move, as well as adding a delay to
     * any transitions used for the active button.
     */
    transitionDuration: {
      type: Number,
      default: () => 500
    }
  },

  components: { NavButton, ProgressBar, ReflectAttrs },

  data: () => ({
    overflowLeft: false,
    overflowRight: false
  }),

  computed: {
    ...mapGetters((vm) => vm[STORE_NAMESPACE_KEY], [
      HIGHEST_UNLOCKED_STEP_INDEX
    ]),

    ...mapState((vm) => vm[STORE_NAMESPACE_KEY], {
      activeStepIndex: ACTIVE_STEP_INDEX,
      steps: STEPS
    })
  },

  methods: {
    ...mapActions((vm) => vm[STORE_NAMESPACE_KEY], {
      nextStep: NEXT_STEP,
      prevStep: PREV_STEP
    }),

    handleOverflow() {
      const { $el } = this;
      if ($el.offsetWidth === $el.scrollWidth) {
        this.overflowLeft = false;
        this.overflowRight = false;
        return;
      }

      this.overflowLeft = $el.scrollLeft > 0;
      this.overflowRight = Math.ceil($el.offsetWidth + $el.scrollLeft) < $el.scrollWidth;
    },

    onResize() {
      onResizeDebounced(this);
    },

    onScroll() {
      handleOverflowDebounced(this);
    },

    scrollToActive() {
      const { $el, $refs } = this;
      if ($el.offsetWidth >= $el.scrollWidth) return;

      const activeItem = $refs.navItem[this.activeStepIndex];
      $el.scrollTo(activeItem.offsetLeft + (activeItem.offsetWidth / 2) - ($el.offsetWidth / 2), 0);
    },

    tryNextStep() {
      if (this.activeStepIndex < this[HIGHEST_UNLOCKED_STEP_INDEX]) this.nextStep();
    }
  },

  watch: {
    activeStepIndex() {
      this.scrollToActive();
    },

    steps() {
      handleOverflowDebounced(this);
    }
  },

  async mounted() {
    window.addEventListener('resize', this.onResize);

    await this.$nextTick();
    this.handleOverflow();
  },

  destroyed() {
    window.removeEventListener('resize', this.onResize);
  }
};
</script>

<style lang="scss" scoped>
.wizard-nav-container {
  overflow-x: auto;

  @media (prefers-reduced-motion: no-preference) {
    scroll-behavior: smooth;
  }

  &.wizard-nav-overflow-left {
    mask-image: linear-gradient(
      90deg,
      rgba(0,0,0,0) 5%,
      rgba(0,0,0,1) 20%);
  }
  &.wizard-nav-overflow-right {
    mask-image: linear-gradient(
      90deg,
      rgba(0,0,0,1) 80%,
      rgba(0,0,0,0) 95%);
  }
  &.wizard-nav-overflow-left.wizard-nav-overflow-right {
    mask-image: linear-gradient(
      90deg,
      rgba(0,0,0,0) 5%,
      rgba(0,0,0,1) 20%,
      rgba(0,0,0,1) 80%,
      rgba(0,0,0,0) 95%);
  }
}

.wizard-nav {
  // Prevent nav items from overlapping by forcing scrolling on smaller
  // screens/wizards with many steps.
  min-width: max-content;
}

.wizard-nav-main {
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
}

.wizard-nav-buttons {
  align-items: center;
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
  text-align: center;
}

.wizard-nav-labels {
  display: flex;
}
.wizard-nav-label {
  margin-top: 0.5em;
  text-align: center;
}

.wizard-prog-bar-track {
  opacity: 0.2;
}
</style>
